import React, { useCallback, useState, useEffect } from 'react'
import { Button, Col, Input, Row, Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import { SelectTerritorialUnits } from '../../../../components/selects'
import SelectTypesOfAgriculturalCrops from '../../../../components/selects/SelectTypesOfAgriculturalCrops'
import {
	FertilizerUsageCrops,
	EntFertilizerUsageCrops,
} from '../../../../models/chemicalization/fertilizerUsage'

interface IProps {
	items: FertilizerUsageCrops[]
	setItems: (items: FertilizerUsageCrops[]) => void
	parentId?: number | null
}

export const FertilizerUsageItems: React.FC<IProps> = ({ items, setItems, parentId }) => {
	const { t } = useTranslation()

	// Состояние для активной вкладки
	const [activeKey, setActiveKey] = useState<string>('')

	useEffect(() => {
		if (items.length > 0 && !activeKey) {
			setActiveKey(items[0].key!)
		}
	}, [items, activeKey])

	// Генерация уникального ключа для районов
	const generateUniqueKey = (): string => crypto.randomUUID()

	// Функция для создания нового объекта культуры (удобрений)
	const createNewCropDetail = (): EntFertilizerUsageCrops => ({
		key: generateUniqueKey(),
		id: null,
		cropArea: null,
		fertilizerArea: null,
		cropTypeId: null,
		percent: null,
		cropType: null,
	})

	// Создаём новый район сразу с одним блоком удобрений
	const createNewDistrict = (): FertilizerUsageCrops => ({
		key: generateUniqueKey(),
		id: null,
		territorialUnitDistrictId: null,
		territorialUnitDistrict: null,
		cropDetails: [createNewCropDetail()],
	})

	// Добавление нового района и переключение на него
	const handleAddDistrict = useCallback(() => {
		const newDistrict = createNewDistrict()
		setItems([...items, newDistrict])
		setActiveKey(newDistrict.key!)
	}, [items, setItems])

	// Удаление района (если их больше одного)
	const handleDeleteDistrict = useCallback(
		(key: string) => {
			if (items.length === 1) return // не разрешаем удалять последний район
			const idx = items.findIndex(d => d.key === key)
			const newItems = items.filter(d => d.key !== key)
			setItems(newItems)
			if (activeKey === key && newItems.length) {
				const nextIndex = idx === 0 ? 0 : idx - 1
				setActiveKey(newItems[nextIndex].key!)
			}
		},
		[items, setItems, activeKey]
	)

	// Изменение поля у района
	const handleDistrictChange = (key: string, field: keyof FertilizerUsageCrops, value: any) => {
		const newItems = items.map(district =>
			district.key === key ? { ...district, [field]: value } : district
		)
		setItems(newItems)
	}

	// Добавление нового объекта культуры в район
	const handleAddCropDetail = (districtKey: string) => {
		const newItems = items.map(district => {
			if (district.key === districtKey) {
				return {
					...district,
					cropDetails: [...(district.cropDetails || []), createNewCropDetail()],
				}
			}
			return district
		})
		setItems(newItems)
	}

	// Удаление объекта культуры из района
	const handleDeleteCropDetail = (districtKey: string, detailIndex: number) => {
		const newItems = items.map(district => {
			if (district.key === districtKey) {
				const updated = [...(district.cropDetails || [])]
				updated.splice(detailIndex, 1)
				return { ...district, cropDetails: updated }
			}
			return district
		})
		setItems(newItems)
	}

	// Изменение полей объекта культуры
	const handleCropDetailChange = (
		districtKey: string,
		detailIndex: number,
		field: keyof EntFertilizerUsageCrops,
		value: any
	) => {
		const newItems = items.map(district => {
			if (district.key === districtKey) {
				const updatedDetails = (district.cropDetails || []).map((detail, idx) => {
					if (idx === detailIndex) {
						return { ...detail, [field]: value }
					}
					return detail
				})
				return { ...district, cropDetails: updatedDetails }
			}
			return district
		})
		setItems(newItems)
	}

	const isFieldEmpty = (value: any) => value === null || value === undefined || value === ''

	return (
		<div style={{ marginTop: 15 }}>
			<Tabs
				type='editable-card'
				activeKey={activeKey}
				onChange={key => setActiveKey(key)}
				onEdit={(key, action) => {
					if (action === 'add') {
						handleAddDistrict()
					} else if (action === 'remove') {
						handleDeleteDistrict(key as string)
					}
				}}
			>
				{items.map(district => (
					<Tabs.TabPane
						key={district.key} // уникальный key
						tab={district.territorialUnitDistrict?.nameRu ?? 'Новый район'}
						closable={items.length > 1}
					>
						<Row gutter={16}>
							<Col span={12}>
								<label>
									{t('titles.territorialUnitDistricts')} <span style={{ color: 'red' }}>*</span>
								</label>
								<SelectTerritorialUnits
									type={['AREA']}
									parentId={parentId}
									value={district.territorialUnitDistrictId}
									status={isFieldEmpty(district.territorialUnitDistrictId) ? 'error' : ''}
									onChange={val =>
										handleDistrictChange(district.key!, 'territorialUnitDistrictId', val)
									}
								/>
							</Col>
						</Row>
						<div style={{ marginTop: 15 }}>
							{district.cropDetails?.map((detail, detailIndex) => (
								<div
									key={detailIndex}
									style={{
										border: '1px solid #ccc',
										padding: 10,
										marginBottom: 10,
										borderRadius: 4,
									}}
								>
									<Row gutter={16}>
										<Col span={8}>
											<label>
												{t('titles.crop_type')} <span style={{ color: 'red' }}>*</span>
											</label>
											<SelectTypesOfAgriculturalCrops
												value={detail.cropTypeId}
												status={isFieldEmpty(detail.cropTypeId) ? 'error' : ''}
												onChange={val =>
													handleCropDetailChange(district.key!, detailIndex, 'cropTypeId', val)
												}
											/>
										</Col>
									</Row>
									<Row gutter={16}>
										<Col span={8}>
											<label>
												{t('chemicalization.fertilizer_usage.crop_area')}{' '}
												<span style={{ color: 'red' }}>*</span>
											</label>
											<Input
												type='number'
												value={detail.cropArea ?? ''}
												className={isFieldEmpty(detail.cropArea) ? 'error-border' : ''}
												onChange={e =>
													handleCropDetailChange(
														district.key!,
														detailIndex,
														'cropArea',
														Number(e.target.value)
													)
												}
											/>
										</Col>
										<Col span={8}>
											<label>
												{t('chemicalization.fertilizer_usage.fertilizer_area')}{' '}
												<span style={{ color: 'red' }}>*</span>
											</label>
											<Input
												type='number'
												value={detail.fertilizerArea ?? ''}
												className={isFieldEmpty(detail.fertilizerArea) ? 'error-border' : ''}
												onChange={e =>
													handleCropDetailChange(
														district.key!,
														detailIndex,
														'fertilizerArea',
														Number(e.target.value)
													)
												}
											/>
										</Col>
									</Row>
									{(district.cropDetails?.length || 0) > 1 && (
										<Row style={{ marginTop: 10 }}>
											<Col span={24}>
												<Button
													danger
													onClick={() => handleDeleteCropDetail(district.key!, detailIndex)}
												>
													{t('general.delete')}
												</Button>
											</Col>
										</Row>
									)}
								</div>
							))}
							<Button type='primary' onClick={() => handleAddCropDetail(district.key!)}>
								+ {t('chemicalization.fertilizer_usage.add_crop')}
							</Button>
						</div>
					</Tabs.TabPane>
				))}
			</Tabs>
		</div>
	)
}
